import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.ilouli.com';

function AiUsageStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/ai-usage/stats?days=${days}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('통계 조회에 실패했습니다.');
      }

      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const formatCost = (cost) => {
    if (!cost) return '$0.00';
    return '$' + cost.toFixed(4);
  };

  if (loading) {
    return (
      <div className="bg-slate-50 rounded-xl p-6 md:p-4">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-500">통계를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-50 rounded-xl p-6 md:p-4">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-2xl font-bold mb-4">!</div>
          <p className="text-gray-500 mb-4">{error}</p>
          <button onClick={fetchStats} className="py-2 px-6 bg-blue-500 text-white border-none rounded-lg cursor-pointer min-h-[40px]">다시 시도</button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="bg-slate-50 rounded-xl p-6 md:p-4">
      {/* 헤더 */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4 md:flex-col md:items-start">
        <h2 className="text-2xl font-semibold text-gray-900 m-0">AI 사용량 통계</h2>
        <div className="flex gap-2 items-center md:w-full">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="py-2 px-4 border border-slate-200 rounded-lg text-sm bg-white cursor-pointer min-h-[40px] md:flex-1"
          >
            <option value={7}>최근 7일</option>
            <option value={14}>최근 14일</option>
            <option value={30}>최근 30일</option>
            <option value={60}>최근 60일</option>
            <option value={90}>최근 90일</option>
          </select>
          <button onClick={fetchStats} className="py-2 px-4 bg-blue-500 text-white border-none rounded-lg text-sm cursor-pointer min-h-[40px] transition-colors duration-200 hover:bg-blue-600">
            새로고침
          </button>
        </div>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 mb-6 md:grid-cols-2 max-[480px]:grid-cols-1">
        <div className="bg-white rounded-lg p-4 flex flex-col gap-2 shadow-sm border border-slate-200">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm text-white bg-gradient-to-br from-blue-500 to-blue-600">API</div>
          <div className="flex flex-col">
            <span className="text-[28px] font-bold text-gray-900 leading-tight">{formatNumber(stats.summary.totalCalls)}</span>
            <span className="text-xs text-gray-500">총 호출 수</span>
          </div>
          <div className="text-xs text-gray-400 pt-1 border-t border-slate-200">
            오늘: {formatNumber(stats.today.calls)}
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 flex flex-col gap-2 shadow-sm border border-slate-200">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm text-white bg-gradient-to-br from-violet-500 to-violet-600">T</div>
          <div className="flex flex-col">
            <span className="text-[28px] font-bold text-gray-900 leading-tight">{formatNumber(stats.summary.totalTokens)}</span>
            <span className="text-xs text-gray-500">총 토큰</span>
          </div>
          <div className="text-xs text-gray-400 pt-1 border-t border-slate-200">
            오늘: {formatNumber(stats.today.tokens)}
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 flex flex-col gap-2 shadow-sm border border-slate-200">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm text-white bg-gradient-to-br from-emerald-500 to-emerald-600">$</div>
          <div className="flex flex-col">
            <span className="text-[28px] font-bold text-gray-900 leading-tight">{formatCost(stats.summary.totalCost)}</span>
            <span className="text-xs text-gray-500">예상 비용</span>
          </div>
          <div className="text-xs text-gray-400 pt-1 border-t border-slate-200">
            오늘: {formatCost(stats.today.cost)}
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 flex flex-col gap-2 shadow-sm border border-slate-200">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-[11px] text-white bg-gradient-to-br from-amber-500 to-amber-600">{stats.summary.successRate}%</div>
          <div className="flex flex-col">
            <span className="text-[28px] font-bold text-gray-900 leading-tight">{stats.summary.uniqueUsers}</span>
            <span className="text-xs text-gray-500">활성 사용자</span>
          </div>
          <div className="text-xs text-gray-400 pt-1 border-t border-slate-200">
            성공률: {stats.summary.successRate}%
          </div>
        </div>
      </div>

      {/* 탭 */}
      <div className="flex gap-1 mb-4 bg-white p-1 rounded-lg border border-slate-200 md:flex-wrap">
        {[
          { id: 'overview', label: '콘텐츠별' },
          { id: 'feature', label: '기능별' },
          { id: 'provider', label: '프로바이더별' },
          { id: 'chart', label: '일별 추이' },
          { id: 'apikey', label: 'API 키별' }
        ].map(tab => (
          <button
            key={tab.id}
            className={`flex-1 py-2 px-4 border-none rounded-md text-sm cursor-pointer transition-all duration-200 min-h-[40px] md:flex-[1_1_45%] max-[480px]:flex-[1_1_100%] ${
              activeTab === tab.id
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-transparent text-gray-500 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 탭 내용 */}
      <div className="bg-white rounded-lg border border-slate-200 min-h-[300px]">
        {activeTab === 'overview' && (
          <div>
            {stats.byContentType.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <span className="text-5xl mb-4">📊</span>
                <p className="text-sm text-gray-500 m-0">아직 사용량 데이터가 없습니다.</p>
              </div>
            ) : (
              stats.byContentType.map((item, index) => (
                <div key={item.contentType} className="grid grid-cols-[32px_40px_1fr_auto] gap-4 items-center p-4 border-b border-slate-200 relative last:border-b-0 md:grid-cols-[28px_32px_1fr] md:gap-2">
                  <div className="w-7 h-7 bg-slate-50 rounded-full flex items-center justify-center text-xs font-semibold text-gray-500">{index + 1}</div>
                  <div className="text-2xl text-center">{item.icon}</div>
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{item.name}</span>
                    <span className="text-xs text-gray-400">{item.contentType}</span>
                  </div>
                  <div className="flex gap-6 md:col-span-full md:justify-between md:pt-2">
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatNumber(item.count)}</span>
                      <span className="text-[11px] text-gray-400">호출</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatNumber(item.tokens)}</span>
                      <span className="text-[11px] text-gray-400">토큰</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{item.successRate}%</span>
                      <span className="text-[11px] text-gray-400">성공률</span>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-4 right-4 h-[3px] bg-slate-200 rounded overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded transition-[width] duration-500"
                      style={{
                        width: `${(item.count / stats.byContentType[0].count) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'feature' && (
          <div>
            {stats.byFeature.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <span className="text-5xl mb-4">📊</span>
                <p className="text-sm text-gray-500 m-0">아직 사용량 데이터가 없습니다.</p>
              </div>
            ) : (
              stats.byFeature.map((item, index) => (
                <div key={item.feature} className="grid grid-cols-[32px_1fr_auto] gap-4 items-center p-4 border-b border-slate-200 relative last:border-b-0 md:gap-2">
                  <div className="w-7 h-7 bg-slate-50 rounded-full flex items-center justify-center text-xs font-semibold text-gray-500">{index + 1}</div>
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{item.name}</span>
                    <span className="text-xs text-gray-400">{item.feature}</span>
                  </div>
                  <div className="flex gap-6 md:col-span-full md:justify-between md:pt-2">
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatNumber(item.count)}</span>
                      <span className="text-[11px] text-gray-400">호출</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatNumber(item.tokens)}</span>
                      <span className="text-[11px] text-gray-400">토큰</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatCost(item.cost)}</span>
                      <span className="text-[11px] text-gray-400">비용</span>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-4 right-4 h-[3px] bg-slate-200 rounded overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-violet-500 to-violet-400 rounded transition-[width] duration-500"
                      style={{
                        width: `${(item.count / stats.byFeature[0].count) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'provider' && (
          <div>
            {stats.byProvider.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <span className="text-5xl mb-4">📊</span>
                <p className="text-sm text-gray-500 m-0">아직 사용량 데이터가 없습니다.</p>
              </div>
            ) : (
              stats.byProvider.map((item, index) => (
                <div key={`${item.provider}-${item.model}`} className="grid grid-cols-[32px_1fr_auto] gap-4 items-center p-4 border-b border-slate-200 relative last:border-b-0 md:gap-2">
                  <div className="w-7 h-7 bg-slate-50 rounded-full flex items-center justify-center text-xs font-semibold text-gray-500">{index + 1}</div>
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{item.provider}</span>
                    <span className="text-xs text-gray-400">{item.model}</span>
                  </div>
                  <div className="flex gap-6 md:col-span-full md:justify-between md:pt-2">
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatNumber(item.count)}</span>
                      <span className="text-[11px] text-gray-400">호출</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatNumber(item.tokens)}</span>
                      <span className="text-[11px] text-gray-400">토큰</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatCost(item.cost)}</span>
                      <span className="text-[11px] text-gray-400">비용</span>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-4 right-4 h-[3px] bg-slate-200 rounded overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded transition-[width] duration-500"
                      style={{
                        width: `${(item.count / stats.byProvider[0].count) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'chart' && (
          <div className="p-4">
            {stats.chartData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <span className="text-5xl mb-4">📊</span>
                <p className="text-sm text-gray-500 m-0">아직 사용량 데이터가 없습니다.</p>
              </div>
            ) : (
              <>
                <div className="h-[250px] flex items-end py-4">
                  <div className="flex items-end gap-0.5 w-full h-full">
                    {stats.chartData.map((day, index) => {
                      const maxCount = Math.max(...stats.chartData.map(d => d.count));
                      const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                      return (
                        <div key={day.date} className="flex-1 h-full flex flex-col items-center justify-end min-w-0">
                          <div
                            className="w-full max-w-[30px] bg-gradient-to-t from-blue-600 to-blue-400 rounded-t min-h-[2px] transition-[height] duration-300 cursor-pointer relative hover:from-blue-700 hover:to-blue-500"
                            style={{ height: `${height}%` }}
                            title={`${day.date}: ${day.count}회, ${formatNumber(day.tokens)} 토큰`}
                          >
                            {day.count > 0 && (
                              <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-gray-500 whitespace-nowrap">{day.count}</span>
                            )}
                          </div>
                          {index % Math.ceil(stats.chartData.length / 10) === 0 && (
                            <span className="text-[10px] text-gray-400 mt-1 whitespace-nowrap">
                              {day.date.slice(5)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="text-center pt-2 border-t border-slate-200 text-xs text-gray-500">
                  <span>일별 API 호출 수</span>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'apikey' && (
          <div>
            {!stats.byApiKey || stats.byApiKey.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <span className="text-5xl mb-4">🔑</span>
                <p className="text-sm text-gray-500 m-0">아직 API 키 사용량 데이터가 없습니다.</p>
              </div>
            ) : (
              stats.byApiKey.map((item, index) => (
                <div key={item.apiKeyId || index} className="grid grid-cols-[32px_40px_1fr_auto] gap-4 items-center p-4 border-b border-slate-200 relative last:border-b-0 md:grid-cols-[28px_32px_1fr] md:gap-2">
                  <div className="w-7 h-7 bg-slate-50 rounded-full flex items-center justify-center text-xs font-semibold text-gray-500">{index + 1}</div>
                  <div className="text-2xl text-center">🔑</div>
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{item.label}</span>
                    <span className="text-xs text-gray-400">{item.provider} / {item.model}</span>
                  </div>
                  <div className="flex gap-6 md:col-span-full md:justify-between md:pt-2">
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatNumber(item.count)}</span>
                      <span className="text-[11px] text-gray-400">호출</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{formatNumber(item.tokens)}</span>
                      <span className="text-[11px] text-gray-400">토큰</span>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 md:items-center">
                      <span className="text-sm font-semibold text-gray-900">{item.lastUsed?.slice(0, 10) || '-'}</span>
                      <span className="text-[11px] text-gray-400">마지막 사용</span>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-4 right-4 h-[3px] bg-slate-200 rounded overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded transition-[width] duration-500"
                      style={{
                        width: `${(item.count / stats.byApiKey[0].count) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AiUsageStats;
